'use client';

import Image from 'next/image';
import Link from '~/(tenant)/_components/tenant-link';
import { PageGlobalStyle } from '~/(tenant)/_components/page-global-style';

import { ArrowRight, MapPin, Clock, Phone, Calendar, Users, Trophy, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import './styles.css';
import enLocale from './locales/en.json';
import arLocale from './locales/ar.json';

const RSS_LOCALES: Record<string, unknown> = {
  en: enLocale,
  ar: arLocale,
};

function resolveLocaleKey(locale: unknown, key: string): string | undefined {
  const segments = key.split('.');
  let current: unknown = locale;
  for (const segment of segments) {
    if (current && typeof current === 'object' && segment in current) {
      current = (current as Record<string, unknown>)[segment];
    } else {
      return undefined;
    }
  }
  return typeof current === 'string' ? current : undefined;
}

function useRSSTranslation() {
  const { i18n } = useTranslation();
  const language = i18n.language?.split('-')[0] ?? 'en';

  return (key: string): string => {
    const localized = resolveLocaleKey(RSS_LOCALES[language], key);
    if (localized !== undefined) return localized;

    const fallback = resolveLocaleKey(enLocale, key);
    return fallback ?? key;
  };
}


// Shield Pattern SVG for RSS branding
function ShieldPattern({ className = '', opacity = 0.1 }: { className?: string; opacity?: number }) {
  return (
    <svg
      className={className}
      viewBox="0 0 100 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ opacity }}
    >
      <path
        d="M50 0L100 20V60C100 90 75 110 50 120C25 110 0 90 0 60V20L50 0Z"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      />
    </svg>
  );
}

// Diagonal Lines Pattern
function DiagonalPattern({ className = '' }: { className?: string }) {
  return (
    <svg className={className} width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="diagonals" width="20" height="20" patternUnits="userSpaceOnUse">
          <line x1="0" y1="20" x2="20" y2="0" stroke="currentColor" strokeWidth="0.5" opacity="0.15" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#diagonals)" />
    </svg>
  );
}

// ============================================
// OPTION 1: ATHLETIC HERITAGE
// Editorial sports magazine aesthetic
// ============================================
function HeritageHero() {
  const t = useRSSTranslation();

  return (
    <section className="relative min-h-screen w-full overflow-hidden bg-[#293044]">
      {/* Background Image with lighter overlay */}
      <div className="absolute inset-0">
        <Image
          src="/landing-pages/rss/images/hero.jpg"
          alt="Red Sea Sports Club aerial view at sunset"
          fill
          className="object-cover"
          priority
        />
        {/* Gradient concentrated at the bottom-left where text sits */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#293044]/90 via-[#293044]/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#293044]/80 via-transparent to-[#293044]/20" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col justify-end min-h-screen pb-24 lg:pb-32">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-16 w-full">
          {/* Est. badge */}
          <span className="font-bobcat text-white/50 text-xs tracking-[3px] uppercase block mb-4">
            {t('hero.estBadge')}
          </span>

          {/* Large Editorial Typography */}
          <div className="mb-8">
            <h1 className="font-deutschlander text-[48px] sm:text-[72px] lg:text-[120px] xl:text-[140px] text-white leading-[0.85] tracking-tight">
              <span className="block">{t('hero.headlineLine1')}</span>
              <span className="block text-[#ED253E]">{t('hero.headlineLine2')}</span>
            </h1>
          </div>

          {/* Subtitle */}
          <p className="font-bobcat text-white/80 text-lg lg:text-xl max-w-[500px] mb-10">
            {t('hero.subheadingLine1')}<br />
            {t('hero.subheadingLine2')}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              href="/book"
              className="inline-flex items-center justify-center bg-[#ED253E] text-white font-deutschlander text-2xl lg:text-3xl h-16 lg:h-18 px-12 hover:bg-[#BE2030] transition-colors duration-300"
            >
              {t('hero.bookClass')}
            </Link>
            <Link
              href="/book"
              className="inline-flex items-center justify-center border-2 border-white text-white font-deutschlander text-2xl lg:text-3xl h-16 lg:h-18 px-12 hover:bg-white/10 transition-colors duration-300"
            >
              {t('hero.reserveCourt')}
            </Link>
          </div>
        </div>
      </div>

      {/* Corner Accent */}
      <div className="absolute top-0 right-0 w-32 h-32 lg:w-48 lg:h-48">
        <div className="absolute top-0 right-0 w-full h-full bg-[#ED253E]" style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 0)' }} />
      </div>
    </section>
  );
}

function HeritageAbout() {
  const t = useRSSTranslation();

  return (
    <section id="about" className="bg-white py-20 lg:py-28">
      <div className="max-w-[1400px] mx-auto px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Content */}
          <div>
            <span className="font-bobcat text-[#8D98AD] text-xs tracking-[3px] uppercase block mb-4">
              {t('about.label')}
            </span>
            <h2 className="font-deutschlander text-[40px] sm:text-[56px] lg:text-[72px] text-[#293044] leading-[0.9] mb-8">
              {t('about.headingLine1')}<br />{t('about.headingLine2')}
            </h2>
            <div className="space-y-6">
              <p className="font-bobcat text-[#293044]/70 text-lg leading-relaxed">
                {t('about.paragraph1')}
              </p>
              <p className="font-bobcat text-[#293044]/70 text-lg leading-relaxed">
                {t('about.paragraph2')}
              </p>
              <p className="font-bobcat text-[#293044]/70 text-lg leading-relaxed">
                {t('about.paragraph3')}
              </p>
            </div>
          </div>

          {/* Right: Image */}
          <div className="relative aspect-[4/5] overflow-hidden">
            <Image
              src="/landing-pages/rss/images/est-2001-team.jpg"
              alt="Red Sea Sports Club early days team photo"
              fill
              className="object-cover" style={{ objectPosition: '20% center' }}
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#293044]/60 to-transparent h-1/3" />
            <div className="absolute bottom-6 left-6 right-6">
              <span className="font-deutschlander text-[24px] text-white">{t('about.imageCaptionEst')}</span>
              <span className="font-bobcat text-white/70 text-sm block">{t('about.imageCaptionLocation')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HeritagePhilosophy() {
  const t = useRSSTranslation();

  return (
    <section className="bg-[#293044] py-20 lg:py-28 relative overflow-hidden">
      {/* Pattern */}
      <div className="absolute inset-0 opacity-5">
        <DiagonalPattern className="text-white" />
      </div>

      <div className="max-w-[1400px] mx-auto px-8 lg:px-16 relative z-10">
        <div className="max-w-[800px] mx-auto text-center">
          <span className="font-bobcat text-[#ED253E] text-xs tracking-[3px] uppercase block mb-4">
            {t('philosophy.label')}
          </span>
          <h2 className="font-deutschlander text-[40px] sm:text-[56px] lg:text-[72px] text-white leading-[0.9] mb-8">
            {t('philosophy.headingLine1')}<br />
            <span className="text-[#8D98AD]">{t('philosophy.headingLine2')}</span>
          </h2>
          <p className="font-bobcat text-white/70 text-lg leading-relaxed mb-6">
            {t('philosophy.paragraph1')}
          </p>
          <p className="font-bobcat text-white/70 text-lg leading-relaxed mb-6">
            {t('philosophy.paragraph2')}
          </p>
          <p className="font-bobcat text-white/70 text-lg leading-relaxed">
            {t('philosophy.paragraph3')}
          </p>
        </div>
      </div>
    </section>
  );
}

function HeritagePrograms() {
  const t = useRSSTranslation();

  const programs = [
    {
      nameKey: 'programs.football',
      image: '/landing-pages/rss/images/football-field.jpeg',
      audienceKeys: ['programs.audiences.children', 'programs.audiences.boys', 'programs.audiences.girls'],
      imagePosition: '',
    },
    {
      nameKey: 'programs.tennis',
      image: '/landing-pages/rss/images/tennis-court-premium.jpg',
      audienceKeys: ['programs.audiences.children', 'programs.audiences.women', 'programs.audiences.allLevels'],
      imagePosition: '60% 35%',
    },
    {
      nameKey: 'programs.padel',
      image: '/landing-pages/rss/images/padel.jpeg',
      audienceKeys: ['programs.audiences.mixed', 'programs.audiences.beginners', 'programs.audiences.advanced'],
      imagePosition: '',
    },
    {
      nameKey: 'programs.baseball',
      image: '/landing-pages/rss/images/baseball.jpg',
      audienceKeys: ['programs.audiences.youth', 'programs.audiences.events'],
      imagePosition: '',
    },
  ];

  return (
    <section id="programs" className="bg-white py-20 lg:py-28">
      <div className="max-w-[1400px] mx-auto px-8 lg:px-16">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16">
          <div>
            <span className="font-bobcat text-[#8D98AD] text-xs tracking-[3px] uppercase block mb-4">
              {t('programs.label')}
            </span>
            <h2 className="font-deutschlander text-[40px] sm:text-[56px] lg:text-[72px] text-[#293044] leading-[0.9]">
              {t('programs.headingLine1')}<br />{t('programs.headingLine2')}
            </h2>
          </div>
          <p className="font-bobcat text-[#293044]/70 text-lg max-w-[400px] mt-6 lg:mt-0">
            {t('programs.intro')}
          </p>
        </div>

        {/* Program Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {programs.map((program, idx) => {
            const programName = t(program.nameKey);
            return (
              <Link
                key={program.nameKey}
                href="/book"
                className="group relative aspect-[4/3] overflow-hidden cursor-pointer block"
              >
                <Image
                  src={program.image}
                  alt={programName}
                  fill
                  className="object-cover transition-transform duration-700 group-hover:scale-105"
                  style={program.imagePosition ? { objectPosition: program.imagePosition } : undefined}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#293044] via-[#293044]/20 to-transparent" />

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="flex items-end justify-between">
                    <div>
                      <h3 className="font-deutschlander text-[32px] lg:text-[48px] text-white leading-none mb-3">
                        {programName}
                      </h3>
                      <div className="flex gap-2 flex-wrap">
                        {program.audienceKeys.map((audienceKey) => (
                          <span
                            key={audienceKey}
                            className="font-bobcat text-xs text-white/80 bg-white/10 px-3 py-1"
                          >
                            {t(audienceKey)}
                          </span>
                        ))}
                      </div>
                    </div>
                    <ArrowRight className="w-8 h-8 text-[#ED253E] transform translate-x-4 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300" />
                  </div>
                </div>

                {/* Number */}
                <div className="absolute top-6 left-6">
                  <span className="font-deutschlander text-[80px] lg:text-[120px] text-white/10 leading-none">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Note */}
        <p className="font-bobcat text-[#8D98AD] text-sm text-center mt-8">
          {t('programs.note')}
        </p>
      </div>
    </section>
  );
}

function HeritageSchedule() {
  const tennisSchedule = [
    { category: 'Girls', ages: '4–5 years', time: '4:00 – 5:00 PM', days: 'Monday & Wednesday' },
    { category: 'Girls & Boys', ages: '4–5 years', time: '5:00 – 6:00 PM', days: 'Monday & Wednesday' },
    { category: 'Girls', ages: '6–7 years', time: '5:00 – 6:00 PM', days: 'Sunday & Tuesday' },
    { category: 'Boys', ages: '6–8 years', time: '6:00 – 7:00 PM', days: 'Sunday & Tuesday' },
    { category: 'Girls & Boys', ages: '9–11 years', time: '6:00 – 7:00 PM', days: 'Thursday & Saturday' },
    { category: 'Boys', ages: '11–14 years', time: '6:00 – 7:00 PM', days: 'Monday & Wednesday' },
    { category: 'Girls', ages: '11–14 years', time: '7:00 – 8:00 PM', days: 'Monday & Wednesday' },
    { category: 'Girls & Boys (Advanced)', ages: '13–15 years', time: '8:00 – 9:00 PM', days: 'Monday & Wednesday' },
  ];

  const footballSchedule = [
    { category: 'Children', ages: '3–5 years', time: '5:00 – 6:00 PM', days: 'Monday & Wednesday' },
    { category: 'Children', ages: '6–8 years', time: '6:00 – 7:00 PM', days: 'Monday & Wednesday' },
    { category: 'Boys', ages: '8–16 years', time: '5:00 – 7:00 PM', days: 'Sunday & Tuesday' },
    { category: 'Girls', ages: '9–16 years', time: '6:00 – 8:00 PM', days: 'Monday & Wednesday' },
  ];

  const t = useRSSTranslation();
  const agesPrefix = t('schedule.agesPrefix');

  const renderGroup = (
    title: string,
    rows: { category: string; ages: string; time: string; days: string }[],
  ) => (
    <div className="mb-16 last:mb-0">
      <h3 className="font-deutschlander text-[28px] sm:text-[36px] lg:text-[44px] text-white mb-8">
        {title}
      </h3>

      {/* Desktop/tablet grid: 2 cols on sm, 4 cols on lg */}
      <div className="max-sm:hidden grid grid-cols-2 lg:grid-cols-4 gap-4">
        {rows.map((row, idx) => (
          <div
            key={`${title}-${idx}`}
            className="bg-white/5 border border-white/10 p-6 hover:bg-white/10 transition-colors duration-300 group"
          >
            <h4 className="font-deutschlander text-[24px] text-white mb-4 group-hover:text-[#ED253E] transition-colors">
              {row.category}
            </h4>
            <div className="space-y-2">
              <div className="flex items-center gap-3 text-white/70 font-bobcat text-sm">
                <Users className="w-4 h-4 text-[#ED253E]" />
                {agesPrefix} {row.ages}
              </div>
              <div className="flex items-center gap-3 text-white/70 font-bobcat text-sm">
                <Clock className="w-4 h-4 text-[#ED253E]" />
                {row.time}
              </div>
              <div className="flex items-center gap-3 text-white/70 font-bobcat text-sm">
                <Calendar className="w-4 h-4 text-[#ED253E]" />
                {row.days}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Mobile: horizontal scroll carousel */}
      <div className="hidden max-sm:flex gap-4 overflow-x-auto snap-x snap-mandatory -mx-8 px-8 pb-4 scrollbar-hide">
        {rows.map((row, idx) => (
          <div
            key={`${title}-mobile-${idx}`}
            className="flex-none w-[280px] snap-start bg-white/5 border border-white/10 p-6"
          >
            <h4 className="font-deutschlander text-[24px] text-white mb-4">
              {row.category}
            </h4>
            <div className="space-y-2">
              <div className="flex items-center gap-3 text-white/70 font-bobcat text-sm">
                <Users className="w-4 h-4 text-[#ED253E]" />
                {agesPrefix} {row.ages}
              </div>
              <div className="flex items-center gap-3 text-white/70 font-bobcat text-sm">
                <Clock className="w-4 h-4 text-[#ED253E]" />
                {row.time}
              </div>
              <div className="flex items-center gap-3 text-white/70 font-bobcat text-sm">
                <Calendar className="w-4 h-4 text-[#ED253E]" />
                {row.days}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section id="schedule" className="bg-[#293044] py-20 lg:py-28 relative overflow-hidden">
      {/* Pattern */}
      <div className="absolute inset-0 opacity-5">
        <DiagonalPattern className="text-white" />
      </div>

      <div className="max-w-[1400px] mx-auto px-8 lg:px-16 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="font-bobcat text-[#ED253E] text-xs tracking-[3px] uppercase block mb-4">
            {t('schedule.label')}
          </span>
          <h2 className="font-deutschlander text-[40px] sm:text-[56px] lg:text-[72px] text-white leading-[0.9]">
            {t('schedule.headingLine1')}<br />
            <span className="text-[#8D98AD]">{t('schedule.headingLine2')}</span>
          </h2>
        </div>

        {renderGroup(t('schedule.footballTitle'), footballSchedule)}
        {renderGroup(t('schedule.tennisTitle'), tennisSchedule)}
      </div>
    </section>
  );
}

function HeritageCommunity() {
  const t = useRSSTranslation();

  return (
    <section className="relative bg-[#ED253E] py-20 lg:py-28 overflow-hidden">
      {/* Shield Patterns */}
      <div className="absolute top-10 left-10 w-32 h-40 text-white">
        <ShieldPattern opacity={0.1} />
      </div>
      <div className="absolute bottom-10 right-10 w-48 h-60 text-white">
        <ShieldPattern opacity={0.1} />
      </div>

      <div className="max-w-[1400px] mx-auto px-8 lg:px-16 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Content */}
          <div className="text-center lg:text-left">
            <span className="font-bobcat text-white/50 text-xs tracking-[3px] uppercase block mb-4">
              {t('community.label')}
            </span>
            <h2 className="font-deutschlander text-[40px] sm:text-[56px] lg:text-[80px] text-white leading-[0.9] mb-6">
              {t('community.heading')}
            </h2>
            <p className="font-bobcat text-white/80 text-lg lg:text-xl max-w-[600px] mx-auto lg:mx-0 mb-8">
              {t('community.paragraph1')}
            </p>
            <p className="font-bobcat text-white/70 text-base max-w-[600px] mx-auto lg:mx-0 mb-4">
              {t('community.paragraph2')}
            </p>
            <p className="font-bobcat text-white/70 text-base max-w-[600px] mx-auto lg:mx-0 mb-10">
              {t('community.paragraph3')}
            </p>
            <div className="flex flex-wrap justify-center lg:justify-start gap-8 mb-10">
              <div className="text-center">
                <Trophy className="w-8 h-8 text-white mx-auto mb-2" />
                <span className="font-bobcat text-white/70 text-sm">{t('community.values.sportsmanship')}</span>
              </div>
              <div className="text-center">
                <Users className="w-8 h-8 text-white mx-auto mb-2" />
                <span className="font-bobcat text-white/70 text-sm">{t('community.values.character')}</span>
              </div>
              <div className="text-center">
                <Calendar className="w-8 h-8 text-white mx-auto mb-2" />
                <span className="font-bobcat text-white/70 text-sm">{t('community.values.discipline')}</span>
              </div>
            </div>
            <p className="font-deutschlander text-[20px] lg:text-[24px] text-white/90 text-center lg:text-left italic">
              {t('community.tagline')}
            </p>
          </div>

          {/* Right: Baseball Action Photos */}
          <div className="grid grid-cols-2 gap-4">
            <div className="relative aspect-[3/4] overflow-hidden">
              <Image
                src="/landing-pages/rss/images/softball-action.jpg"
                alt="Softball player batting at Red Sea Sports Club"
                fill
                className="object-cover"
              />
            </div>
            <div className="relative aspect-[3/4] overflow-hidden mt-8">
              <Image
                src="/landing-pages/rss/images/baseball-catch.jpg"
                alt="Young player catching a fly ball"
                fill
                className="object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HeritageFacilities() {
  const t = useRSSTranslation();

  return (
    <section id="facilities" className="bg-[#EAF6F6] py-20 lg:py-28">
      <div className="max-w-[1400px] mx-auto px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Image */}
          <div className="relative aspect-[4/3] overflow-hidden order-2 lg:order-1">
            <Image
              src="/landing-pages/rss/images/facilities.jpg"
              alt="Red Sea Sports Club facilities aerial view"
              fill
              className="object-cover"
            />
          </div>

          {/* Right: Content */}
          <div className="order-1 lg:order-2">
            <span className="font-bobcat text-[#8D98AD] text-xs tracking-[3px] uppercase block mb-4">
              {t('facilities.label')}
            </span>
            <h2 className="font-deutschlander text-[40px] sm:text-[56px] lg:text-[72px] text-[#293044] leading-[0.9] mb-6">
              {t('facilities.heading')}
            </h2>
            <div className="space-y-4">
              <p className="font-bobcat text-[#293044]/70 text-lg leading-relaxed">
                {t('facilities.paragraph1')}
              </p>
              <p className="font-bobcat text-[#293044]/70 text-lg leading-relaxed">
                {t('facilities.paragraph2')}
              </p>
              <p className="font-bobcat text-[#293044]/70 text-lg leading-relaxed">
                {t('facilities.paragraph3')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HeritageVenueBooking() {
  const t = useRSSTranslation();

  return (
    <section id="venue-booking" className="bg-[#293044] py-20 lg:py-28 relative overflow-hidden">
      {/* Pattern */}
      <div className="absolute inset-0 opacity-5">
        <DiagonalPattern className="text-white" />
      </div>

      <div className="max-w-[1400px] mx-auto px-8 lg:px-16 relative z-10">
        <div className="max-w-[800px] mx-auto text-center">
          <span className="font-bobcat text-[#ED253E] text-xs tracking-[3px] uppercase block mb-4">
            {t('venueBooking.label')}
          </span>
          <h2 className="font-deutschlander text-[40px] sm:text-[56px] lg:text-[72px] text-white leading-[0.9] mb-8">
            {t('venueBooking.headingLine1')}<br />
            <span className="text-[#8D98AD]">{t('venueBooking.headingLine2')}</span>
          </h2>
          <p className="font-bobcat text-white/70 text-lg leading-relaxed mb-6">
            {t('venueBooking.paragraph1')}
          </p>
          <p className="font-bobcat text-white/70 text-lg leading-relaxed mb-12">
            {t('venueBooking.paragraph2')}
          </p>

          {/* Contact CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/966535073502?text=Hi%2C%20I%27d%20like%20to%20inquire%20about%20venue%20booking"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center bg-[#25D366] text-white font-deutschlander text-2xl lg:text-3xl h-16 lg:h-18 px-12 hover:bg-[#1da851] transition-colors duration-300 gap-3"
            >
              <MessageCircle className="w-6 h-6" />
              {t('venueBooking.whatsappCta')}
            </a>
            <a
              href="tel:+966535073502"
              className="inline-flex items-center justify-center border-2 border-white text-white font-deutschlander text-2xl lg:text-3xl h-16 lg:h-18 px-12 hover:bg-white/10 transition-colors duration-300 gap-3"
            >
              <Phone className="w-6 h-6" />
              {t('venueBooking.callCta')}
            </a>
          </div>

          <p className="font-bobcat text-[#8D98AD] text-sm mt-8">
            {t('venueBooking.footer')}
          </p>
        </div>
      </div>
    </section>
  );
}

function HeritageLocation() {
  const t = useRSSTranslation();

  return (
    <section id="location" className="bg-white py-20 lg:py-28">
      <div className="max-w-[1400px] mx-auto px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left: Info */}
          <div>
            <span className="font-bobcat text-[#8D98AD] text-xs tracking-[3px] uppercase block mb-4">
              {t('location.label')}
            </span>
            <h2 className="font-deutschlander text-[40px] sm:text-[56px] text-[#293044] leading-[0.9] mb-8">
              {t('location.heading')}
            </h2>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#ED253E] flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-deutschlander text-[20px] text-[#293044] mb-1">{t('location.addressTitle')}</h3>
                  <p className="font-bobcat text-[#293044]/70">
                    {t('location.addressLine1')}<br />
                    {t('location.addressLine2')}<br />
                    {t('location.addressLine3')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#ED253E] flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-deutschlander text-[20px] text-[#293044] mb-1">{t('location.contactTitle')}</h3>
                  <a href="tel:+966535073502" className="font-bobcat text-[#ED253E] hover:underline">
                    +966 53 507 3502
                  </a>
                </div>
              </div>
            </div>

            <a
              href="https://maps.app.goo.gl/7roqnPhEt1hcCqKm8"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center bg-[#293044] text-white font-deutschlander text-2xl lg:text-3xl h-16 lg:h-18 px-12 mt-10 hover:bg-[#293044]/90 transition-colors"
            >
              {t('location.directionsCta')}
              <ArrowRight className="w-5 h-5 ml-3" />
            </a>
          </div>

          {/* Right: Map */}
          <div className="aspect-square lg:aspect-auto min-h-[400px] overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5357.143058244146!2d39.114765012047265!3d21.589587968195268!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x15c3db9f3135d92b%3A0x1a424addacf6cfd1!2sRed%20Sea%20Sports%20Club!5e0!3m2!1sen!2ssa!4v1768982529245!5m2!1sen!2ssa"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '400px' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Red Sea Sports Club Location"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================
export default function RSSLandingPage() {
  return (
    <div className="min-h-screen overflow-x-hidden">
      <PageGlobalStyle>{`
        html {
          scroll-behavior: smooth;
        }

        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }
      `}</PageGlobalStyle>

      <HeritageHero />
      <HeritageAbout />
      <HeritagePhilosophy />
      <HeritagePrograms />
      <HeritageSchedule />
      <HeritageCommunity />
      <HeritageFacilities />
      <HeritageVenueBooking />
      <HeritageLocation />
    </div>
  );
}
